import { MapPin, Network, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

import type { MarketHub } from "@/types/market-intelligence";

/**
 * What kind of pricing point this is, in market terms: how Market
 * Intelligence labels every point in the sidebar, the region overview, the
 * point's own header, the region grids and Market Movers (working session
 * with the power desk, 2026-10-01).
 *
 * - **Hub** (`Network`): a trading point. You can buy forward power here, but
 *   load never settles at a hub.
 * - **Load zone** (`MapPin`): where load settles, at the day-ahead or
 *   real-time index.
 *
 * Two kinds, not three. A load zone you can also buy forward power at (most of
 * PJM; the desk's "both") is still a load zone. What it publishes is a
 * separate fact, shown by the Forwards, Index and Basis chips on its card and
 * by the tabs on its page, so a third icon would say again what those already
 * say.
 *
 * **Gas has the same two, under its own names** (2026-10-02):
 *
 * - **Hub** (`Network`, the same icon): a supply-area trading hub — Henry Hub,
 *   the NYMEX delivery point every gas basis is quoted against.
 * - **Delivery point** (`MapPin`, the load zone's icon): a market-area
 *   citygate or delivered pipeline zone, where gas reaches the local utility
 *   and a customer's gas is priced. The load zone's counterpart, so it shares
 *   its icon; its label says which market it belongs to.
 */
export const POINT_KINDS: Record<
  MarketHub["kind"],
  { icon: LucideIcon; label: string; description: string }
> = {
  hub: {
    icon: Network,
    label: "Hub",
    description:
      "A trading hub: forward prices are quoted here. Load does not settle at a hub.",
  },
  load_zone: {
    icon: MapPin,
    label: "Load zone",
    description: "A load zone: load settles here, at the day-ahead or real-time index.",
  },
  delivery_point: {
    icon: MapPin,
    label: "Delivery point",
    description:
      "A gas delivery point: a citygate or delivered pipeline zone, where gas reaches the local utility. Priced as a basis to Henry Hub.",
  },
};

/** The kind's icon, named for assistive tech and explained on hover. */
export function PointKindIcon({
  kind,
  size = 14,
  className,
  title = true,
}: {
  kind: MarketHub["kind"];
  size?: number;
  className?: string;
  /** Hover text explaining the kind. Off where a visible label says it already. */
  title?: boolean;
}) {
  const { icon: Icon, label, description } = POINT_KINDS[kind];
  return (
    <span
      className={cn("inline-flex shrink-0", className)}
      title={title ? `${label}. ${description}` : undefined}
      role="img"
      aria-label={label}
    >
      <Icon size={size} strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
}
